/**
 * Centralized admin email allowlist for the React frontend.
 *
 * Adding or removing an admin requires editing this constant, the matching
 * backend Set in website/src/admin.ts, and the rules files (firestore.rules
 * and storage.rules). Long-term migration to a Firebase custom claim is
 * tracked as audit finding F49.
 */
export const ADMIN_EMAILS = [
  'scott@advantagehorsemanship.com',
  'allie@advantagehorsemanship.com',
] as const

/**
 * Ask Reese first-run allowlist (Scott, Oct 14 2026). These emails may use Ask Reese
 * on the Overview only. They do NOT get full admin. Match against the signed-in
 * Firebase Auth user email with emailVerified true. Never trust a client-sent email field.
 * Finance deny for employees is enforced by the workforce API policy, not here.
 */
export const ASK_REESE_EMAILS = [
  'allie@advantagehorsemanship.com',
  'helen.advantagehorsemanship@gmail.com',
] as const

/**
 * Case-insensitive admin allowlist check. Defends against case mismatch if
 * Firebase Auth ever returns a mixed-case email.
 */
export function isAdminEmail(email: string | null | undefined): boolean {
  if (!email) return false
  return (ADMIN_EMAILS as readonly string[]).includes(email.toLowerCase())
}

/** True when the signed-in Auth email is on ASK_REESE_EMAILS (case-insensitive). */
export function isAskReeseEmail(email: string | null | undefined): boolean {
  if (!email) return false
  return (ASK_REESE_EMAILS as readonly string[]).includes(email.toLowerCase())
}

/**
 * Ask Reese UI access from the verified Firebase Auth user only.
 * Requires emailVerified === true. Does not read request bodies or form fields.
 */
export function canUseAskReese(user: { email?: string | null; emailVerified?: boolean } | null | undefined): boolean {
  if (!user || user.emailVerified !== true) return false
  return isAskReeseEmail(user.email)
}
